// /:partnerId/login — form de login.
//
// Flujo:
//   1. Lee AuthParams del query (puesto por /authorize del backend).
//   2. Usuario ingresa email+password → POST /auth/login con los params.
//   3. Backend autentica, genera authorization_code, responde redirect_to.
//   4. SPA hace window.location.href = redirect_to (vuelve al partner con el code).
//
// Si no hay AuthParams → el usuario llego aca sin pasar por /authorize.
// Muestra un estado explicativo. No falla silenciosamente: sin AuthParams
// no tenemos adonde mandarlo.

import { FormEvent, useState } from 'react';
import { normalizeEmail } from '@/lib/email-normalize';
import { Link, useParams } from 'react-router-dom';
import AuthShell from '@/components/AuthShell';
import FormField from '@/components/FormField';
import SubmitButton from '@/components/SubmitButton';
import { useAuthParams } from '@/lib/auth-params';
import { FlowExitLink } from '@/components/FlowNav';
import {
  login,
  resendVerification,
  isConsentRequired,
  ApiError,
  type ConsentRequired,
} from '@/lib/api';
import { makeVerifyNonce, setVerifyCookie } from '@/lib/verify-nonce';
import ConsentTerms from '@/components/ConsentTerms';
import { webmailLink } from '@/lib/webmail';

function errorMessageFor(code: string): string {
  switch (code) {
    case 'user_not_found':
    case 'invalid_credentials':
      // Mismo mensaje en los dos casos — anti-enumeracion de usuarios.
      return 'Email o contraseña incorrectos.';
    case 'user_blocked':
      return 'Tu cuenta está bloqueada. Contactá al soporte.';
    case 'invalid_request_body':
      return 'Datos inválidos. Revisá el formulario.';
    case 'rate_limited':
      return 'Demasiados intentos. Esperá un momento antes de reintentar.';
    case 'network_error':
      return 'No pudimos conectar. Revisá tu conexión e intentá de nuevo.';
    case 'internal_error':
      return 'Tuvimos un problema de nuestro lado. Probá de nuevo en un momento.';
    default:
      return 'No pudimos completarlo. Probá de nuevo en un momento.';
  }
}

export default function Login(): JSX.Element {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const authz = useAuthParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Re-consentimiento de T&C (ADR 0008): si el login lo pide, mostramos el
  // interstitial inline en vez del form.
  const [consent, setConsent] = useState<ConsentRequired | null>(null);
  // Email no verificado: el backend rechaza el login con `email_not_verified`.
  // Mostramos un panel con la opcion de reenviar el correo de verificacion.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  );
  // CTA al webmail del usuario tras el reenvío (Gmail abre la búsqueda del
  // remitente, que incluye Spam).
  const resendMail = resendState === 'sent' ? webmailLink(email) : null;

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!authz.ok) return; // Defensivo — el boton esta deshabilitado igual.
    setLoading(true);
    setErr(null);
    setNeedsVerification(false);
    setResendState('idle');
    try {
      const result = await login({
        // Capa 3 — submit: se vuelve a forzar la forma canónica. Idempotente
        // respecto de las capas del input; cubre lo que las haya salteado.
        email: normalizeEmail(email),
        password,
        partner_id: partnerId,
        ...authz.params,
      });
      if (isConsentRequired(result)) {
        setConsent(result);
        setLoading(false);
        return;
      }
      window.location.href = result.redirect_to;
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      if (code === 'email_not_verified') {
        // Credenciales OK pero email sin verificar: ofrecemos reenviar.
        setNeedsVerification(true);
      } else {
        setErr(errorMessageFor(code));
      }
      setLoading(false);
    }
  }

  async function handleResend(): Promise<void> {
    setResendState('sending');
    try {
      // Generamos un nonce de navegador (este) y lo guardamos en cookie: el
      // link reenviado lo lleva, asi al abrirlo aca completamos auto-login.
      const verifyNonce = makeVerifyNonce();
      setVerifyCookie(verifyNonce);
      await resendVerification(partnerId, {
        email: normalizeEmail(email),
        verify_nonce: verifyNonce,
        ...(authz.ok && {
          client_id: authz.params.client_id,
          redirect_uri: authz.params.redirect_uri,
          state: authz.params.state,
          code_challenge: authz.params.code_challenge,
          code_challenge_method: 'S256',
          scope: authz.params.scope,
          ...(authz.params.nonce && { nonce: authz.params.nonce }),
        }),
      });
      setResendState('sent');
    } catch {
      setResendState('error');
    }
  }

  if (consent) {
    return (
      <ConsentTerms
        partnerId={partnerId}
        ticket={consent.consent_ticket}
        tos={consent.tos}
        onResolved={(r) => {
          if ('redirect_to' in r) window.location.href = r.redirect_to;
        }}
      />
    );
  }

  if (!authz.ok) {
    return (
      <AuthShell
        title="Link inválido"
        subtitle="Este enlace está incompleto. Volvé a la aplicación e iniciá sesión desde ahí."
      >
        <div
          role="alert"
          aria-live="polite"
          className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error text-center"
        >
          Faltan parámetros de autorización: {authz.missing.join(', ')}.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Ingresar"
      subtitle="Usá tu email y contraseña."
      footerLinks={
        <>
          <Link to={`/${partnerId}/signup${window.location.search}`} className="hover:underline">
            Crear cuenta
          </Link>
          <Link to={`/${partnerId}/forgot-password${window.location.search}`} className="hover:underline">
            Olvidé mi contraseña
          </Link>
        </>
      }
      flowExit={
        <FlowExitLink
          exit={{
            kind: 'oauth',
            redirectUri: authz.params.redirect_uri,
            state: authz.params.state,
          }}
        />
      }
    >
      <form
        onSubmit={onSubmit}
        aria-busy={loading ? 'true' : undefined}
        className="space-y-4"
        noValidate
      >
        <FormField
          name="email"
          type="email"
          label="Email"
          autoComplete="username"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <FormField
          name="password"
          type="password"
          label="Contraseña"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {err ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
          >
            {err}
          </div>
        ) : null}
        {needsVerification ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm text-text space-y-2"
          >
            <p>
              Tu e-mail no está verificado todavía. Revisá tu casilla y hacé clic
              en el link de verificación que te enviamos para poder ingresar.
            </p>
            {resendState === 'sent' ? (
              <div className="space-y-2">
                <p className="text-primary">
                  Te reenviamos el correo de verificación. Revisá tu casilla (y spam).
                  Los links anteriores siguen sirviendo: cualquiera activa tu cuenta.
                </p>
                {resendMail ? (
                  <a
                    href={resendMail.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block rounded-md border border-accent px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/10"
                  >
                    Abrir {resendMail.label}
                  </a>
                ) : null}
              </div>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resendState === 'sending'}
                className="underline hover:no-underline disabled:opacity-60"
              >
                {resendState === 'sending'
                  ? 'Reenviando…'
                  : 'Reenviar correo de verificación'}
              </button>
            )}
            {resendState === 'error' ? (
              <p className="text-error">
                No pudimos reenviarlo ahora. Probá de nuevo en un momento.
              </p>
            ) : null}
          </div>
        ) : null}
        <SubmitButton loading={loading} loadingLabel="Ingresando…">
          Ingresar
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
